// Slice 2C2A · Document Defaults page — four shared family cards. (Slice
// 2C2B adds per-family localStorage draft persistence: baselines are
// tracked here, the current user id is forwarded so drafts are scoped
// per authenticated user, and a per-family refetch is exposed so the
// shared card can re-fetch the latest saved values on discard or during
// the pre-save server-conflict check.)
import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import DocumentDefaultsFamilyCard from "@/components/docs/DocumentDefaultsFamilyCard";
import {
  DOCUMENT_FAMILIES,
  DOCUMENT_DEFAULTS_KEYS,
} from "@/components/docs/documentDefaultsFamilies";

const emptyForm = DOCUMENT_DEFAULTS_KEYS.reduce((acc, k) => {
  acc[k] = "";
  return acc;
}, {});

const DocumentDefaults = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const userId = user?.id || "";
  const [values, setValues] = useState(emptyForm);
  // Baseline = last server-hydrated OR last-saved payload. Kept
  // separate from `values` so the shared card can detect “unsaved
  // changes” via strict equality.
  const [baseline, setBaseline] = useState(emptyForm);
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/document-defaults")
      .then((r) => {
        if (cancelled) return;
        const { updated_at, ...rest } = r.data || {};
        const next = { ...emptyForm, ...rest };
        setValues(next);
        setBaseline(next);
        setBaselineUpdatedAt(updated_at || "");
      })
      .catch(() => toast.error(t("document_defaults_page.toast_failed")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Slice 2C2A · Family-scoped change handler. Only touches the one
  // key the user is editing — never any other family's state.
  const onChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const replaceFamilyValues = useCallback((familyValues) => {
    setValues((prev) => ({ ...prev, ...familyValues }));
  }, []);

  const replaceFamilyBaseline = useCallback((familyValues, updatedAt) => {
    setBaseline((prev) => ({ ...prev, ...familyValues }));
    if (updatedAt) setBaselineUpdatedAt(updatedAt);
  }, []);

  // Per-family refetch. Slice 2C2B blocker fix: this is READ-ONLY.
  // It returns the family's latest server slice so the card can run its
  // pre-save server-conflict check or its discard-refetch, but it must
  // NEVER mutate the global baseline. Baseline updates only happen
  // inside sanctioned card handlers via onBaselineReplace, which means
  // a passive refetch cannot cascade a value replacement into any
  // dirty family (Proposal remains dirty when Contract is saved
  // elsewhere; Contract remains dirty when this tab checks server state
  // before its own save; etc.).
  const refetchFor = useCallback((fam) => async () => {
    const r = await api.get("/document-defaults");
    const { updated_at, ...rest } = r.data || {};
    const merged = { ...emptyForm, ...rest };
    const familySlice = {};
    for (const f of fam.fields) familySlice[f.key] = merged[f.key] || "";
    return { values: familySlice, updated_at: updated_at || "" };
  }, []);

  const familyBaselines = useMemo(() => {
    // Each family's baseline slice, precomputed for cheap prop equality.
    const out = {};
    for (const fam of DOCUMENT_FAMILIES) {
      const slice = {};
      for (const f of fam.fields) slice[f.key] = baseline[f.key] || "";
      out[fam.key] = slice;
    }
    return out;
  }, [baseline]);

  return (
    <DashboardLayout
      title={t("document_defaults_page.title")}
      subtitle={t("document_defaults_page.subtitle")}
    >
      <div className="max-w-4xl space-y-6" data-testid="document-defaults-page">
        {loading ? (
          <div className="p-6 text-sm text-[var(--jc-text-soft)]">
            {t("document_defaults_page.saving")}
          </div>
        ) : (
          DOCUMENT_FAMILIES.map((fam) => (
            <DocumentDefaultsFamilyCard
              key={fam.key}
              familyKey={fam.key}
              title={fam.title}
              purpose={fam.purpose}
              snapshotNote={fam.snapshotNote}
              fields={fam.fields}
              values={values}
              onChange={onChange}
              endpoint={fam.endpoint}
              saveButtonLabel={fam.saveButtonLabel}
              successMessage={fam.successMessage}
              userId={userId}
              baseline={familyBaselines[fam.key]}
              baselineUpdatedAt={baselineUpdatedAt}
              onBaselineReplace={replaceFamilyBaseline}
              onFamilyValuesReplace={replaceFamilyValues}
              refetchFamily={refetchFor(fam)}
            />
          ))
        )}
      </div>
    </DashboardLayout>
  );
};

export default DocumentDefaults;
